import React, { useCallback, useState } from "react";
import { ActionsMenuItem } from "./ActionsMenuItem";
import { ActionsMenuItemLabel } from "./ActionsMenuItemLabel";
import { ActionTypeIcons, ActionTypes } from "../../../util/ActionUtils";

/** Props every dialog passed to `DialogActionItem` must accept. */
export interface ActionDialogProps<TPayload = void> {
  open: boolean;
  onClose: (payload?: TPayload) => void;
}

interface DialogActionItemProps<TPayload, TDialogProps extends object> {
  label: string;
  icon?: React.ReactNode;
  postIcon?: React.ReactNode;
  show?: boolean;
  enable?: boolean;
  dialog: React.ComponentType<ActionDialogProps<TPayload> & TDialogProps>;
  dialogProps?: TDialogProps;
  onDialogClose?: (payload?: TPayload) => void;
}

/**
 * Action menu item that opens a dialog when clicked and reports the dialog's
 * close payload to `onDialogClose`. Renders like `ActionItem`.
 * The dialog stays mounted under the menu, so the parent menu must be `keepMounted` (as `ActionsMenu` is).
 */
const DialogActionItem = <TPayload = void, TDialogProps extends object = object>({
  label,
  icon,
  postIcon,
  show = true,
  enable = true,
  dialog: Dialog,
  dialogProps,
  onDialogClose,
}: DialogActionItemProps<TPayload, TDialogProps>) => {
  const [open, setOpen] = useState(false);

  const handleClick = () => {
    if (!show || !enable) return;
    setOpen(true);
  };

  const handleClose = useCallback((payload?: TPayload) => {
    setOpen(false);
    onDialogClose?.(payload);
  }, [onDialogClose]);

  if (!show) return null;

  return (
    <>
      <ActionsMenuItem onClick={handleClick} disabled={!enable}>
        {icon}
        <ActionsMenuItemLabel>{label}</ActionsMenuItemLabel>
        {postIcon ? postIcon : ActionTypeIcons[ActionTypes.DIALOG]}
      </ActionsMenuItem>
      <Dialog {...(dialogProps as TDialogProps)} open={open} onClose={handleClose} />
    </>
  );
};

export { DialogActionItem };
